
import {computed, defineComponent, onMounted, onUnmounted, PropType, ref, watch,} from "vue";
import Multiselect from "@/Components/General/Multiselect.vue";
import Category from "@/types/Category";
import Faq from "@/types/Faq";
import PaginationType from "@/types/Pagination";
import {debounce} from "lodash";
import Card from "@/Components/General/Card.vue";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";
import ButtonGroup from "@/Components/Buttons/ButtonGroup.vue";
import FAQList from "@/Components/Faq/FAQList.vue";
import {Option} from "@/types/LevelsSelectTypes";
import Checkbox from "@/Components/Controls/Checkbox.vue";
import {Link, useForm} from "@inertiajs/inertia-vue3";
import Pagination from "@/Components/General/Pagination.vue";
import AppLayout from "@/Layouts/AppLayout.vue";
import FaqCategory from "@/types/FaqCategory";
import {Inertia} from "@inertiajs/inertia";
import Messages from "@/Components/General/Messages.vue";
import HcButton from "@/Components/Buttons/Button.vue";
import route from "ziggy-js";
dayjs.extend(calendar);

export default defineComponent({
  methods: {route},
  layout: AppLayout,
  components: {
    HcButton,
    Messages,
    Checkbox,
    Card,
    Link,
    Pagination,
    Multiselect,
    ButtonGroup,
    FAQList,
  },
  props: {
    faqs: {
      type: Object as PropType<PaginationType<Faq>>,
      required: true,
    },
    search: {
      type: String,
      default: () => "",
    },
    is_draft: {
      type: Boolean,
      default: () => false,
    },
    category: {
      type: Object as PropType<FaqCategory>,
      required: false,
    },
    categories: {
      type: Array as PropType<FaqCategory[]>,
      required: true,
    },

  },
  setup(props, context) {
    const form = useForm({
      search: props.search,
      is_draft: props.is_draft,
      faq_category: props.category?.id
    });
    const category = ref(props.category ? {
      id: props.category.id,
      title: props.category.title,
      parent_id: props.category.parent_id,
      options: mapCategories(props.category.sub_categories as any, 2),
    } : null)
    const selectedOptionId = ref(category.value?.id ?? 0);
    const selectedCategory = ref(props.category);

    function mapCategories(categories: FaqCategory[], levels: number): Option[] {

      if (levels == 0) {
        return [];

      }
      return categories.map((category: FaqCategory) => ({
        id: category.id,
        label: category.title,
        parent_id: category.parent_id,
        expandable: category.sub_categories && category.sub_categories.length > 0,
        options: category.sub_categories ? mapCategories(category.sub_categories, levels - 1) : [],
      }))
    }

    const options = mapCategories([
      {
        id: 0,
        title: "Alle kategorier",
        sub_categories: [],
        parent_id: null,
      },
      ...props.categories] as any, 3);
    const goToCategory = () => {
      if (!selectedOptionId.value || selectedOptionId.value === 0) {
        Inertia.get(route("faq.index", {
          search: form.search,
          is_draft: form.is_draft
        }));
        return;
      }

      Inertia.get(route("faq.index", {
        faq_category: selectedOptionId.value,
        search: form.search,
        is_draft: form.is_draft,
      }));
    };

    watch(
      () => selectedOptionId.value,
      function () {

        goToCategory();

      }
    );

    const getRelativeDate = (date: string) => {
      return dayjs(date).calendar();
    };

    const getAbsoluteDate = (date: string) => {
      return dayjs(date).format("L LT");
    };

    const submit = debounce(() => {
      Inertia.reload({
        data: {
          search: form.search,
          is_draft: form.is_draft,
        },
        preserveState: true,
        replace: true,
      });
    }, 250);

    watch(() => form.search, submit);
    watch(() => form.is_draft, submit);


    const displayCategories = computed(() => {
      return [
        {
          id: null,
          title: "Alle kategorier",
          expandable: false
        } as any,

        ...props.categories,
      ];
    });

    const desktopCategory = computed({
      get: () => {
        return props.category ?? null;
      },
      set: (v) => {
        goToCategory();
      },
    });
    const selectCategory = (category: FaqCategory) => {
      desktopCategory.value = category;
    };

    const paginationSize = ref("sm");

    const setPaginationSize = () => {
      paginationSize.value = "sm";

      if (window.innerWidth >= 768) {
        paginationSize.value = "md";
      }

      if (window.innerWidth >= 1080) {
        paginationSize.value = "lg";
      }
    };

    onMounted(() => {
      setPaginationSize();
      window.addEventListener("resize", debounce(setPaginationSize, 150));
    });

    onUnmounted(() => {
      window.removeEventListener("resize", debounce(setPaginationSize, 150));
    });

    return {
      form,
      Link,
      goToCategory,
      paginationSize,
      desktopCategory,
      getRelativeDate,
      getAbsoluteDate,
      displayCategories,
      selectedCategory,
      selectCategory,
      options,
      selectedOptionId
    };
  },
});
